import React, { useState } from 'react';
import { useAssets } from '../../hooks/useInvestQueries';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../App';
import AppLayout from '../../components/layouts/AppLayout';
import ResponsiveNavbar from '../../components/ResponsiveNavbar';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function AdminAssets() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: assets = [], isLoading: loading, error } = useAssets();
  const [showDialog, setShowDialog] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', total_value: '', min_percentage: '', max_percentage: 100,
    images: [], videos: [], documents: [], visibility: true
  });

  if (error) {
    toast.error('Failed to load assets');
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAsset) {
        await api.put(`/assets/${editingAsset.asset_id}`, formData);
        toast.success('Asset updated');
      } else {
        await api.post('/assets', formData);
        toast.success('Asset created');
      }
      setShowDialog(false);
      setEditingAsset(null);
      setFormData({ name: '', description: '', total_value: '', min_percentage: '', max_percentage: 100, images: [], videos: [], documents: [], visibility: true });
      queryClient.invalidateQueries(['assets']);
      queryClient.invalidateQueries(['admin-stats']);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (assetId) => {
    if (!confirm('Are you sure you want to delete this asset?')) return;
    try {
      await api.delete(`/assets/${assetId}`);
      toast.success('Asset deleted');
      queryClient.invalidateQueries(['assets']);
      queryClient.invalidateQueries(['admin-stats']);
    } catch (error) {
      toast.error('Deletion failed');
    }
  };

  const openEdit = (asset) => {
    setEditingAsset(asset);
    setFormData({
      name: asset.name, description: asset.description, total_value: asset.total_value,
      min_percentage: asset.min_percentage, max_percentage: asset.max_percentage,
      images: asset.images || [], videos: asset.videos || [], documents: asset.documents || [],
      visibility: asset.visibility
    });
    setShowDialog(true);
  };

  return (
    <AppLayout>
      <div className="min-h-screen" data-testid="admin-assets-page">
        {/* Responsive Navigation */}
        <ResponsiveNavbar isAdmin={true} />

        {/* Content */}
        <div className="container-responsive max-w-7xl mx-auto py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold">Manage Assets</h1>
          <button onClick={() => { setEditingAsset(null); setFormData({ name: '', description: '', total_value: '', min_percentage: '', max_percentage: 100, images: [], videos: [], documents: [], visibility: true }); setShowDialog(true); }} className="w-full sm:w-auto px-4 py-2.5 sm:px-6 sm:py-3 text-sm sm:text-base bg-[#D4AF37] text-[#0A1128] font-bold rounded-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all flex items-center justify-center gap-2" data-testid="create-asset-btn">
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Create Asset</span>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto"></div></div>
        ) : assets.length === 0 ? (
          <div className="glass-panel p-12 text-center"><p className="text-[#64748B]">No assets created yet</p></div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {assets.map(asset => (
              <div key={asset.asset_id} className="premium-card overflow-hidden hover:scale-[1.02] transition-all" data-testid={`asset-${asset.asset_id}`}>
                {/* Asset Image */}
                <div className="w-full h-48 overflow-hidden">
                  {asset.images && asset.images.length > 0 ? (
                    <img 
                      src={asset.images[0]} 
                      alt={asset.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%230F172A" width="400" height="300"/%3E%3Ctext fill="%2364748B" font-family="Arial" font-size="24" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-16 h-16 text-[#64748B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  {/* Visibility Badge */}
                  <div className="absolute top-3 right-3 z-10">
                    {asset.visibility ? (
                      <div className="px-3 py-1.5 bg-[#10B981] text-[#0A1128] font-bold rounded-lg shadow-[0_4px_12px_rgba(16,185,129,0.4)] flex items-center gap-2 transform transition-transform hover:scale-105" title="Visible to Investors">
                        <Eye className="w-4 h-4" />
                        <span className="text-xs uppercase tracking-wider">Visible</span>
                      </div>
                    ) : (
                      <div className="px-3 py-1.5 bg-[#64748B] text-white font-bold rounded-lg shadow-[0_4px_12px_rgba(100,116,139,0.4)] flex items-center gap-2 transform transition-transform hover:scale-105" title="Hidden from Investors">
                        <EyeOff className="w-4 h-4" />
                        <span className="text-xs uppercase tracking-wider">Hidden</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <h3 className="font-['Outfit'] text-xl font-bold mb-2">{asset.name}</h3>
                  <p className="text-[#94A3B8] text-sm mb-4 line-clamp-2">{asset.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm"><span className="text-[#94A3B8]">Total Value</span><span className="font-['JetBrains_Mono'] font-semibold">${asset.total_value.toLocaleString()}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-[#94A3B8]">Committed</span><span className="font-['JetBrains_Mono'] font-semibold text-[#F59E0B]">{asset.percent_committed}%</span></div>
                    <div className="flex justify-between text-sm"><span className="text-[#94A3B8]">Available</span><span className="font-['JetBrains_Mono'] font-semibold text-[#10B981]">{asset.percent_remaining}%</span></div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(asset)} className="flex-1 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors flex items-center justify-center gap-2" data-testid={`edit-asset-btn-${asset.asset_id}`}>
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button onClick={() => handleDelete(asset.asset_id)} className="flex-1 py-2 bg-[#EF4444]/20 hover:bg-[#EF4444]/30 text-[#EF4444] rounded-lg transition-colors flex items-center justify-center gap-2" data-testid={`delete-asset-btn-${asset.asset_id}`}>
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#0F172A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-2xl font-bold">{editingAsset ? 'Edit Asset' : 'Create New Asset'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Asset Name *</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none" required data-testid="asset-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description *</label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="3" className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none" required data-testid="asset-description-input" />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Total Value ($) *</label>
                <input type="number" value={formData.total_value} onChange={(e) => setFormData({ ...formData, total_value: Number(e.target.value) })} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none" required data-testid="asset-value-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Min % *</label>
                <input type="number" step="0.1" value={formData.min_percentage} onChange={(e) => setFormData({ ...formData, min_percentage: Number(e.target.value) })} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none" required data-testid="asset-min-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Max %</label>
                <input type="number" step="0.1" value={formData.max_percentage} onChange={(e) => setFormData({ ...formData, max_percentage: Number(e.target.value) })} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none" data-testid="asset-max-input" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Image URLs (comma-separated)</label>
              <input type="text" value={formData.images.join(', ')} onChange={(e) => setFormData({ ...formData, images: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none" placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg" data-testid="asset-images-input" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={formData.visibility} onChange={(e) => setFormData({ ...formData, visibility: e.target.checked })} className="w-4 h-4" data-testid="asset-visibility-input" />
              <label className="text-sm">Visible to investors</label>
            </div>
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={() => setShowDialog(false)} className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">Cancel</button>
              <button type="submit" className="flex-1 py-3 bg-[#D4AF37] text-[#0A1128] font-bold rounded-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all" data-testid="save-asset-btn">{editingAsset ? 'Update' : 'Create'} Asset</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      </div>
    </AppLayout>
  );
}
